import raw from "./data/products.json";
import { drinkCarbs, type Discipline, type Line, type LineKind, type Product } from "./calc.js";
import type { Lang } from "./i18n.js";

/**
 * The product catalogue.
 *
 * ACCEPTED AS-IS on 2026-07-31 (open point O-2, closed by decision). The values
 * come from the client's spreadsheet and were cross-checked against a later
 * revision of it: every gel and bar figure matched exactly. The drink per-litre
 * figures could not be extracted from that revision and are unverified.
 *
 * Treat this file as data owned by the client, not by us: change it when they
 * send a new workbook, not to fix a number that looks off.
 */

export interface CatalogueProduct extends Product {
  /** Only the generic entries carry one -- brand names stay as they are. */
  nameEn?: string;
}

export interface Catalogue {
  meta: {
    source: string;
    note: string;
    constants: Record<string, number>;
  };
  drinks: CatalogueProduct[];
  gels: CatalogueProduct[];
  bars: CatalogueProduct[];
}

export const CATALOGUE = raw as Catalogue;

export type GroupKey = "drinks" | "gels" | "bars";

export const GROUP_KIND: Record<GroupKey, LineKind> = {
  drinks: "drink",
  gels: "gel",
  bars: "bar",
};

/**
 * `flame` gel, `candy` bar, `droplet` drink.
 *
 * The handoff shipped `cookie` for Riegel and the designer flagged it as the
 * weakest glyph in the set -- it reads as a biscuit, not a bar. Decided
 * 2026-08-01 in favour of `candy`: a wrapped item is what a bar looks like in a
 * jersey pocket, and it keeps the food metaphor that `flame` and `droplet` set.
 */
export const KIND_ICON: Record<LineKind, string> = {
  drink: "droplet",
  gel: "flame",
  bar: "candy",
};

export function productName(product: CatalogueProduct, lang: Lang): string {
  return lang === "en" ? (product.nameEn ?? product.name) : product.name;
}

export function findProduct(group: GroupKey, id: string): CatalogueProduct | undefined {
  return CATALOGUE[group].find((p) => p.id === id);
}

/**
 * Drinks a discipline can actually use (FR-2.8). Running works from cups, so it
 * only offers per-litre products (or zero-carb ones like water); everything else
 * works per prepared portion.
 */
export function drinksForDiscipline(discipline: Discipline): CatalogueProduct[] {
  return CATALOGUE.drinks.filter((p) =>
    discipline === "run" ? p.carbsPerLitre != null || p.carbs === 0 : p.carbs != null,
  );
}

export function groupCatalogue(group: GroupKey, discipline: Discipline): CatalogueProduct[] {
  return group === "drinks" ? drinksForDiscipline(discipline) : CATALOGUE[group];
}

export interface VesselContext {
  discipline: Discipline;
  bottleMl: number;
  cupMl: number;
}

/**
 * Build a line from a product. Drinks store only the id and re-derive their
 * carbs from the current vessel, so changing bottle or cup size rescales them
 * (FR-2.7).
 */
export function makeLine(
  group: GroupKey,
  product: CatalogueProduct,
  vessel: VesselContext,
  count = 1,
  lang: Lang = "de",
): Line {
  return {
    kind: GROUP_KIND[group],
    id: product.id,
    name: productName(product, lang),
    carbs: group === "drinks" ? drinkCarbs(product, vessel) : (product.carbs ?? 0),
    kcal: product.kcal ?? 0,
    count,
  };
}

/** Carbs a product contributes in this context -- shown next to its name. */
export function carbsOf(
  group: GroupKey,
  product: CatalogueProduct,
  vessel: VesselContext,
): number {
  return group === "drinks" ? drinkCarbs(product, vessel) : (product.carbs ?? 0);
}
